import React, { useCallback, useMemo } from 'react';
import { _ } from '@splunk/ui-utils/i18n';
import { noop } from '@splunk/dashboard-utils';
import { useImageRegistry } from '@splunk/dashboard-context';
import { Gallery, type Item, type GalleryProps } from '../Gallery';
import { GallerySearch } from '../GallerySearch';
import { GalleryMessage } from '../GalleryMessage';
import ImageGalleryItem from './ImageGalleryItem';
import { GalleryUpload } from '../GalleryUpload';
import {
    type ResourceRegistry,
    useRegistryGallery,
    type RegistryItemMapper,
} from '../hooks/useRegistryGallery';

export interface ImageItem {
    id: string;
    filename?: string;
    metaData: {
        name: string;
    };
}

interface ImageGalleryProps {
    providerType?: string;
    onClick?: (itemUrl: Item['url']) => void;
    enableSearch?: boolean;
    setHasChildModal?: GalleryProps['setHasChildModal'];
}

export const ImageGallery = ({
    providerType: initialProviderType,
    onClick = noop,
    enableSearch = true,
    setHasChildModal,
}: ImageGalleryProps) => {
    const registry = useImageRegistry() as ResourceRegistry<ImageItem>;
    const listIdsOptions = useMemo(() => ({ meta: ['name'] }), []);
    const mapImageItem: RegistryItemMapper<ImageItem> = useCallback((idObj) => {
        const rawName =
            idObj.filename ?? idObj.metaData?.name ?? _('Unnamed image');
        const name =
            typeof rawName === 'string' && rawName.includes('.')
                ? rawName.split('.')[0]
                : rawName;

        return {
            url: `${idObj.providerType}://${idObj.id}`,
            name,
        };
    }, []);
    const {
        items: images,
        loading,
        searching,
        error,
        warning,
        handleUpload,
        handleSearch,
        handleDelete,
    } = useRegistryGallery<ImageItem>({
        registry,
        initialProviderType,
        missingRegistryMessage: _('No image registry found'),
        missingProviderMessage: _('No image providers found'),
        emptyWarningMessage: _('No images. Please upload images.'),
        emptySearchWarningMessage: _('No images found.'),
        listIdsOptions,
        mapItem: mapImageItem,
    });

    const handleItemClick = useCallback(
        (url: string) => onClick(url),
        [onClick]
    );

    const search = useMemo(() => {
        return <GallerySearch onSearch={handleSearch} />;
    }, [handleSearch]);

    const message = useMemo(
        () => <GalleryMessage error={error} warning={warning} />,
        [error, warning]
    );

    const upload = useMemo(
        () => (
            <GalleryUpload
                fileTypes=".jpeg,.png,.gif"
                onUpload={handleUpload}
            />
        ),
        [handleUpload]
    );

    return (
        <Gallery
            testId="image-gallery"
            isLoading={loading || searching}
            // Do not display search input if it is explicitly disabled, there is an error,
            // or images have not been initialized (null)
            search={enableSearch && !error && images ? search : null}
            message={message}
            items={images}
            Item={ImageGalleryItem}
            upload={error ? null : upload}
            onItemClick={handleItemClick}
            onItemRemove={handleDelete}
            setHasChildModal={setHasChildModal}
            galleryType="image"
        />
    );
};
